import { CompanyOutreachSection } from "./ui/CompanyOutreach";
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { Building2, Users, Ban, MailWarning, Pencil, Trash2, UserPlus, FileText, MapPin, Layers, ScrollText, BadgeCheck, ListChecks } from "lucide-react";
import { getPrimeCompany, upsertPrimeCompany, saveCompanyNaicsReview, reopenCompany, type PrimeCompanyDetail, type PrimeCompany, type TextListField, type SetPrimaryResult } from "./api/CrmApi";
import ContractorTypeBadge from "./ui/ContractorTypeBadge";
import RoleVerdictButtons from "./ui/RoleVerdictButtons";
import { ContractorType } from "./api/CrmApi";
import NaicsCodesInput from "./ui/NaicsCodesInput";
import NaicsSuggestionsBox from "./ui/NaicsSuggestionsBox";
import CompanyNotesSection from "./ui/CompanyNotesSection";
import EditCompanyModal from "./ui/EditCompanyModal";
import AddPrimeContactModal from "./ui/AddPrimeContactModal";
import PrimaryContactToggle from "./ui/PrimaryContactToggle";
import RemoveContactModal from "./ui/RemoveContactModal";
import DeleteCompanyModal from "./ui/DeleteCompanyModal";
import { COMPANY_STATUS_LABELS, isCompanySkipStatus, type CompanySkipStatus } from "./companyStatus";
import CompanySkipStatusModal from "./ui/CompanySkipStatusModal";
import { apiErrorMessage } from "./apiError";
import EditAddressesModal from "./ui/EditAddressesModal";
import EditServiceCategoriesModal from "./ui/EditServiceCategoriesModal";
import EditCapabilityStatementsModal from "./ui/EditCapabilityStatementsModal";
import EditTextListModal from "./ui/EditTextListModal";
import { TEXT_LISTS, TEXT_LIST_FIELDS } from "./textLists";
import EditCredentialsModal from "./ui/EditCredentialsModal";
import { amountSummary, CORE_KINDS } from "./credentialNames";
import { locatedHint } from "./ui/LocationsEditor";
import { ensureHttpUrl, formatDate } from "./ui/crmUi";
import PageHeader from "shared/PageHeader";
import { useBackTo, rowLinkState, useHighlightedRow, rowDomId, ROW_HIGHLIGHT_CLASS } from "shared/listNavigation";
import Button from "shared/Button";
import StatusPill from "shared/StatusPill";
import Alert from "shared/Alert";
import { TABLE_SHELL, TBODY_ROW, TD, TH, THEAD_ROW } from "shared/tableStyles";

function formatDownloaded(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function formatRevenue(n: number | null): string {
  return n === null ? "—" : `$${n.toLocaleString("en-US")}`;
}

// An office's parts joined for display. Sheet addresses often already contain the city / state / country,
// so a part is skipped when the text before it already holds it (display only — the data is untouched).
function formatOffice(l: { address: string | null; city: string | null; state: string | null; zip: string | null; country: string | null }): string {
  let out = "";
  for (const part of [l.address, l.city, l.state, l.zip, l.country]) {
    if (!part) continue;
    // Whole-word match, so "ON" is not "found" inside "Toronto".
    if (new RegExp(`(^|[^\\w])${part.trim().replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}($|[^\\w])`, "i").test(out)) continue;
    out = out ? `${out}, ${part}` : part;
  }
  return out;
}

function FactRow({ label, children, align = "right" }: { label: string; children: React.ReactNode; align?: "left" | "right" }) {
  return (
    <div className="flex justify-between gap-3 py-1.5 border-b border-border last:border-0 text-sm">
      <span className="text-muted-foreground shrink-0">{label}</span>
      <span className={`text-foreground min-w-0 break-words ${align === "left" ? "text-left" : "text-right"}`}>{children}</span>
    </div>
  );
}

const CREDENTIAL_GROUP_LABELS = {
  certificate: "Certificates", licence: "Licences", bond: "Bonds", insurance: "Insurance",
  vehicle: "Contract vehicles / registrations", business_cert: "Business certifications",
} as const;

export default function CompanyDetailPage() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const crmBasePath = location.pathname.startsWith("/employee") ? "/employee/crm" : "/admin/crm";
  // Back goes to the exact list URL we came from (Companies page N, the review
  // queue, …) and asks it to highlight this company's row.
  const backTo = useBackTo(`${crmBasePath}/companies`, "Back to Companies", id ? Number(id) : undefined);

  const [company, setCompany] = useState<PrimeCompanyDetail | null>(null);
  const [loading, setLoading] = useState(true);
  // Row to highlight when returning here from a contact's profile page.
  const highlightId = useHighlightedRow("prime", !loading && company !== null);
  const [error, setError] = useState("");

  const [codes, setCodes] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [skipStatusOpen, setSkipStatusOpen] = useState<CompanySkipStatus | null>(null);
  const [reopening, setReopening] = useState(false);
  const [statusError, setStatusError] = useState("");
  const [addContactOpen, setAddContactOpen] = useState(false);
  const [removeContactId, setRemoveContactId] = useState<number | null>(null);
  const [addressesOpen, setAddressesOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [capabilityOpen, setCapabilityOpen] = useState(false);
  const [textListOpen, setTextListOpen] = useState<TextListField | null>(null);
  const [credentialsOpen, setCredentialsOpen] = useState(false);
  const [naicsReviewSaving, setNaicsReviewSaving] = useState(false);
  const [naicsReviewError, setNaicsReviewError] = useState("");

  const load = () => {
    if (!id) return;
    setLoading(true);
    setError("");
    getPrimeCompany(Number(id))
      .then((data) => {
        setCompany(data);
        setCodes(data.naics_codes ?? []);
      })
      .catch((e) => setError(e.response?.data?.detail || "Failed to load company"))
      .finally(() => setLoading(false));
  };

  useEffect(load, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  async function reopen() {
    if (!company) return;
    setReopening(true);
    setStatusError("");
    try {
      await reopenCompany(company.id);
      load();
    } catch (err: any) {
      setStatusError(apiErrorMessage(err, "Could not re-open the company. Please try again."));
    } finally {
      setReopening(false);
    }
  }

  async function saveCapability() {
    if (!company || codes.length === 0) return;
    setSaving(true);
    setSaveError(null);
    try {
      const updated = await upsertPrimeCompany({
        company_id: company.id,
        naics_codes: codes,
      });
      setCompany((prev) => (prev ? { ...prev, ...updated } : prev));
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
    } catch (err: any) {
      setSaveError(err?.response?.data?.detail || err?.message || "Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  // The server changed one contact's mark (and maybe took it from another): mirror that without reloading the page.
  const handlePrimaryChanged = (result: SetPrimaryResult) => {
    setCompany((prev) =>
      prev
        ? {
            ...prev,
            primary_contact_id: result.is_primary ? result.id : prev.primary_contact_id === result.id ? null : prev.primary_contact_id,
            prime_contacts: prev.prime_contacts.map((p) =>
              p.id === result.id ? { ...p, is_primary: result.is_primary } : result.replaced && p.id === result.replaced.id ? { ...p, is_primary: false } : p
            ),
          }
        : prev
    );
  };

  const handleNotesSaved = (updated: PrimeCompany) => {
    setCompany((prev) => (prev ? { ...prev, ...updated } : prev));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !company) {
    return <Alert tone="danger">{error || "Company not found"}</Alert>;
  }

  const handleNaicsDecided = (updated: PrimeCompany) => {
    setCompany((prev) => (prev ? { ...prev, ...updated } : prev));
    // an approved suggestion is now a real code: add it to what is typed, never replace unsaved edits
    setCodes((prev) => Array.from(new Set([...prev, ...(updated.naics_codes ?? [])])));
  };

  async function markNaicsReviewed(value: boolean) {
    if (!company) return;
    setNaicsReviewSaving(true);
    setNaicsReviewError("");
    try {
      const updated = await saveCompanyNaicsReview(company.id, value);
      setCompany((prev) => (prev ? { ...prev, ...updated } : prev));
    } catch (err: any) {
      setNaicsReviewError(err?.response?.data?.detail || err?.message || "Could not save. Please try again.");
    } finally {
      setNaicsReviewSaving(false);
    }
  }

  const handleEditSaved = (updated: PrimeCompany) => {
    setCompany((prev) => (prev ? { ...prev, ...updated } : prev));
  };

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        backTo={backTo}
        title={
          <span className="inline-flex items-center gap-3">
            <span className="flex-shrink-0 w-10 h-10 rounded-xl bg-primary-soft flex items-center justify-center">
              <Building2 className="w-5 h-5 text-primary" />
            </span>
            {company.name || "(unnamed company)"}
          </span>
        }
        description={company.domain || "no resolved domain"}
        badge={company.created_manually ? <StatusPill tone="info" size="sm" title="Added by hand with the Add company button">Added manually</StatusPill> : undefined}
        actions={
          <div className="flex items-center gap-2">
            <Button variant="brand-outline" size="sm" onClick={() => setEditOpen(true)} title="Edit company details">
              <Pencil className="w-3.5 h-3.5" /> Edit
            </Button>
            {isCompanySkipStatus(company.status) ? (
              <Button variant="outline" size="sm" onClick={reopen} disabled={reopening} title="Set the company back to active so future RFPs pick it up again">
                {reopening ? "Re-opening…" : "Re-open"}
              </Button>
            ) : company.status === "active" ? (
              <>
                <Button variant="outline" size="sm" onClick={() => setSkipStatusOpen("not_a_fit")} title="This company does no software-related work at all: skip it for future RFPs">
                  <Ban className="w-3.5 h-3.5" /> Not a fit
                </Button>
                <Button variant="outline" size="sm" onClick={() => setSkipStatusOpen("inactive")} title="This company no longer exists: skip it for future RFPs">
                  Inactive
                </Button>
              </>
            ) : null}
            <Button variant="danger" size="sm" onClick={() => setDeleteOpen(true)} title="Delete this company, its contacts and its RFP links">
              <Trash2 className="w-3.5 h-3.5" /> Delete
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="min-w-0">
          <p className="text-xs font-medium text-muted-foreground mb-2">Company Facts</p>
          <div className="bg-surface border border-border rounded-xl px-4 py-2">
            <FactRow label="Main Domain">
              {company.domain ? (
                <a href={`https://${company.domain}`} target="_blank" rel="noreferrer" className="text-primary hover:underline">
                  {company.domain}
                </a>
              ) : "—"}
            </FactRow>
            <FactRow label="Other Websites">
              {company.additional_domains?.length ? (
                <span className="inline-flex flex-wrap justify-end gap-x-3 gap-y-1">
                  {company.additional_domains.map((d) => (
                    <a key={d} href={`https://${d}`} target="_blank" rel="noreferrer" className="text-primary hover:underline">
                      {d}
                    </a>
                  ))}
                </span>
              ) : "—"}
            </FactRow>
            <FactRow label="LinkedIn">
              {company.linkedin_url ? (
                <a href={ensureHttpUrl(company.linkedin_url)} target="_blank" rel="noreferrer" className="text-primary hover:underline">
                  {company.linkedin_url}
                </a>
              ) : "—"}
            </FactRow>
            <FactRow label="US confirmed">{company.us_confirmed ? "Yes — an office in the United States" : "No office in the United States on file"}</FactRow>
            <FactRow label="Size">{company.employee_range || "—"}</FactRow>
            <FactRow label="Annual revenue">
              {formatRevenue(company.annual_revenue)}
              {company.annual_revenue !== null && company.revenue_source && (
                <span className="block text-xs text-muted-foreground">{company.revenue_source}</span>
              )}
            </FactRow>
            <FactRow label="NIGP codes">{company.nigp_codes.length > 0 ? company.nigp_codes.join(", ") : "—"}</FactRow>
            <FactRow label="Organization type">{company.org_type || "—"}</FactRow>
            <FactRow label="Status">
              <StatusPill tone={company.status === "active" ? "success" : company.status === "do_not_contact" ? "danger" : "neutral"} dot>
                {COMPANY_STATUS_LABELS[company.status] ?? company.status}
              </StatusPill>
              {isCompanySkipStatus(company.status) && (
                <span className="block text-xs text-muted-foreground" data-testid="company-status-reason">
                  {company.status_reason || "No reason on file"}
                  {company.status_set_at ? ` · ${formatDate(company.status_set_at, true)}` : ""}
                  {company.status_set_by ? ` · ${company.status_set_by.replace(":", " #")}` : ""}
                  {" · skipped for future RFPs; confirmed primes on past RFPs are unchanged"}
                </span>
              )}
              {statusError && <span className="block text-xs text-destructive" role="alert">{statusError}</span>}
            </FactRow>
            <FactRow label="Role on RFPs">
              Prime on {company.prime_rfp_count}
              {company.unconfirmed_rfp_count > 0 && (
                <span className="text-muted-foreground"> · role unconfirmed on {company.unconfirmed_rfp_count} RFP{company.unconfirmed_rfp_count === 1 ? "" : "s"}</span>
              )}
              {company.sub_appearance_count > 0 && (
                <span className="text-muted-foreground"> · also appeared as a sub-contractor on {company.sub_appearance_count} RFP{company.sub_appearance_count === 1 ? "" : "s"}</span>
              )}
            </FactRow>
            <FactRow label="Replies">
              {(company.replies ?? []).length === 0 ? (
                <span className="text-muted-foreground">No reply from anyone at this company yet.</span>
              ) : (
                <ul className="flex flex-col gap-1" aria-label="RFPs where this company replied">
                  {(company.replies ?? []).map((r) => (
                    <li key={r.opportunity_id}>
                      <Link to={`${crmBasePath}?opportunity_id=${r.opportunity_id}`} className="text-primary hover:underline">{r.title || `RFP ${r.opportunity_id}`}</Link>
                      <span className="text-muted-foreground"> · {formatDate(r.replied_at, true)}{r.contact_email ? ` · ${r.contact_email}` : ""}</span>
                      {r.reply_text && <span className="block text-xs text-muted-foreground">“{r.reply_text}”</span>}
                    </li>
                  ))}
                </ul>
              )}
            </FactRow>
            {company.sub_appearances.length > 0 && (
              <FactRow label="Sub-contractor on">
                <ul className="flex flex-col gap-0.5" aria-label="RFPs where this company appeared only as a sub-contractor">
                  {company.sub_appearances.map((a) => (
                    <li key={a.opportunity_id}>
                      <Link to={`${crmBasePath}?opportunity_id=${a.opportunity_id}`} className="text-primary hover:underline">{a.title}</Link>
                    </li>
                  ))}
                </ul>
              </FactRow>
            )}
          </div>

          <div className="flex items-center justify-between mt-5 mb-2">
            <p className="text-xs font-medium text-muted-foreground">Office addresses</p>
            <Button variant="brand-outline" size="sm" onClick={() => setAddressesOpen(true)} title="Edit this company's office addresses">
              <MapPin className="w-3.5 h-3.5" /> Edit addresses
            </Button>
          </div>
          <div className="bg-surface border border-border rounded-xl px-4 py-2" data-testid="office-addresses">
            {company.locations.length > 0 ? (
              <ul className="divide-y divide-border">
                {company.locations.map((l, i) => {
                  const hint = locatedHint(l);
                  return (
                    <li key={`${l.address ?? ""}-${l.city ?? ""}-${l.state ?? ""}-${l.country ?? ""}-${i}`} className="py-2 first:pt-1 last:pb-1 text-sm">
                      <div>{formatOffice(l) || "—"}</div>
                      <div className="mt-0.5 flex items-center gap-1.5 flex-wrap">
                        {l.is_hq && <StatusPill tone="info" size="sm">HQ</StatusPill>}
                        <StatusPill tone="neutral" size="sm">{l.source}</StatusPill>
                        {l.staffed === "staffed_office" && <StatusPill tone="success" size="sm">staffed</StatusPill>}
                        {l.staffed === "mailing_address_only" && (
                          <StatusPill tone="warning" size="sm" title="Ignored by local-presence checks">mailing address only</StatusPill>
                        )}
                        {hint && (
                          <StatusPill tone={hint.located ? "neutral" : "orange"} size="sm">{hint.text}</StatusPill>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="py-1 text-sm text-muted-foreground">
                {company.offices_reviewed_at ? `Researched — no address found (${formatDate(company.offices_reviewed_at, true)}).` : "No offices on file."}
              </p>
            )}
          </div>

          <div className="mt-5 bg-surface border border-border rounded-xl px-4 py-3" data-testid="notes-block">
            <CompanyNotesSection companyId={company.id} notes={company.notes ?? ""} onSaved={handleNotesSaved} />
          </div>
        </div>

        <div className="min-w-0">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-medium text-muted-foreground">Capability statement</p>
            <Button variant="brand-outline" size="sm" onClick={() => setCapabilityOpen(true)} title="Edit what this company will and will not take">
              <ScrollText className="w-3.5 h-3.5" /> Edit capability statement
            </Button>
          </div>
          <div className="bg-surface border border-border rounded-xl px-4 py-3 space-y-3" data-testid="capability-statements">
            {(company.capability_statements ?? []).length > 0 ? (
              company.capability_statements!.map((s, i) => (
                <div key={i}>
                  {s.label && <p className="text-[11px] font-medium text-muted-foreground mb-0.5">{s.label}</p>}
                  <p className="text-sm text-foreground whitespace-pre-line break-words">{s.text}</p>
                </div>
              ))
            ) : (
              company.capability_basis === "reviewed_none" ? (
                <p className="text-sm text-muted-foreground">Researched — nothing found{company.capability_reviewed_at ? ` (${formatDate(company.capability_reviewed_at, true)})` : ""}.</p>
              ) : (
                <p className="text-sm text-muted-foreground">No capability statement yet — the fit checks fall back to the notes.</p>
              )
            )}
          </div>

          <div className="flex items-center justify-between mt-5 mb-2">
            <p className="text-xs font-medium text-muted-foreground">Service categories</p>
            <Button variant="brand-outline" size="sm" onClick={() => setServicesOpen(true)} title="Edit what this company does">
              <Layers className="w-3.5 h-3.5" /> Edit service categories
            </Button>
          </div>
          <div className="bg-surface border border-border rounded-xl px-4 py-3" data-testid="service-categories">
            {(company.service_categories ?? []).length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {company.service_categories.map((s) => (
                  <StatusPill key={s.text} tone={s.source === "sheet" ? "neutral" : "brand"} size="sm" title={s.source === "sheet" ? "From the CollabP sheet — unconfirmed" : undefined}>
                    {s.text}{s.source === "sheet" ? " · from sheet" : ""}
                  </StatusPill>
                ))}
              </div>
            ) : company.service_basis === "reviewed_none" ? (
              <p className="text-sm text-muted-foreground">Researched — nothing found{company.service_categories_reviewed_at ? ` (${formatDate(company.service_categories_reviewed_at, true)})` : ""}.</p>
            ) : (
              <p className="text-sm text-muted-foreground">Not researched yet.</p>
            )}
            {company.service_basis === "research" && company.service_categories_reviewed_at && (
              <p className="mt-2 text-[11px] text-muted-foreground">Researched {formatDate(company.service_categories_reviewed_at, true)}</p>
            )}
          </div>

          <div className="flex items-center justify-between mt-5 mb-2">
            <p className="text-xs font-medium text-muted-foreground">Credentials</p>
            <Button variant="brand-outline" size="sm" onClick={() => setCredentialsOpen(true)} title="Edit the licences, certificates, bonds, insurance, contract vehicles and business certifications this company holds">
              <BadgeCheck className="w-3.5 h-3.5" /> Edit credentials
            </Button>
          </div>
          <div className="bg-surface border border-border rounded-xl px-4 py-3 space-y-2" data-testid="credentials">
            {(company.credentials ?? []).length > 0 ? (
              (["certificate", "licence", "bond", "insurance", "vehicle", "business_cert"] as const).map((kind) => {
                const group = (company.credentials ?? []).filter((c) => c.kind === kind);
                if (group.length === 0) return null;
                return (
                  <div key={kind}>
                    <p className="text-[11px] font-medium text-muted-foreground mb-1">{CREDENTIAL_GROUP_LABELS[kind]}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {group.map((c) => {
                        const tone = c.status === "lacks" ? "danger" : c.expired ? "warning" : c.source === "sheet" ? "neutral" : "success";
                        const amounts = amountSummary(c.kind, c.amount_usd, c.aggregate_usd);
                        const notes = [c.status === "lacks" ? "does not hold" : null, amounts || null, c.expired ? `expired ${c.expires_on}` : c.expires_on ? `expires ${c.expires_on}` : null, c.source === "sheet" ? "from sheet" : null].filter(Boolean);
                        return (
                          <StatusPill key={`${c.kind}:${c.name}`} tone={tone} size="sm" title={[c.detail, c.source === "sheet" ? "From the CollabP sheet — unconfirmed" : null].filter(Boolean).join(" — ") || undefined}>
                            {c.name}{notes.length ? ` · ${notes.join(", ")}` : ""}
                          </StatusPill>
                        );
                      })}
                    </div>
                  </div>
                );
              })
            ) : company.credentials_basis === "reviewed_none" ? (
              <p className="text-sm text-muted-foreground">Researched — nothing found{company.credentials_reviewed_at ? ` (${formatDate(company.credentials_reviewed_at, true)})` : ""}.</p>
            ) : (
              <p className="text-sm text-muted-foreground">Not researched yet.</p>
            )}
            {(company.credentials ?? []).length > 0 && !(company.credentials ?? []).some((c) => CORE_KINDS.includes(c.kind)) && !company.credentials_reviewed_at && (
              <p className="text-[11px] text-muted-foreground">Licences and certificates: not researched yet.</p>
            )}
            {(company.credentials ?? []).length > 0 && !(company.credentials ?? []).some((c) => CORE_KINDS.includes(c.kind)) && company.credentials_reviewed_at && (
              <p className="text-[11px] text-muted-foreground">Licences and certificates: researched — nothing found ({formatDate(company.credentials_reviewed_at, true)}).</p>
            )}
            {company.credentials_basis === "research" && company.credentials_reviewed_at && (company.credentials ?? []).some((c) => CORE_KINDS.includes(c.kind)) && (
              <p className="text-[11px] text-muted-foreground">Researched {formatDate(company.credentials_reviewed_at, true)}</p>
            )}
          </div>

          {TEXT_LIST_FIELDS.map((field) => {
            const cfg = TEXT_LISTS[field];
            const entries = company[field] ?? [];
            const basis = company[`${field}_basis`];
            const markedAt = company[`${field}_reviewed_at`];
            return (
              <div key={field}>
                <div className="flex items-center justify-between mt-5 mb-2">
                  <p className="text-xs font-medium text-muted-foreground">{cfg.title}</p>
                  <Button variant="brand-outline" size="sm" onClick={() => setTextListOpen(field)} title={cfg.hint}>
                    <ListChecks className="w-3.5 h-3.5" /> {cfg.edit}
                  </Button>
                </div>
                <div className="bg-surface border border-border rounded-xl px-4 py-3 space-y-3" data-testid={cfg.testid}>
                  {entries.length > 0 ? (
                    entries.map((e, i) => (
                      <div key={i}>
                        {e.label && <p className="text-[11px] font-medium text-muted-foreground mb-0.5">{e.label}</p>}
                        <p className="text-sm text-foreground whitespace-pre-line break-words">{e.text}</p>
                      </div>
                    ))
                  ) : basis === "reviewed_none" ? (
                    <p className="text-sm text-muted-foreground">Researched — nothing found{markedAt ? ` (${formatDate(markedAt, true)})` : ""}.</p>
                  ) : (
                    <p className="text-sm text-muted-foreground">{cfg.empty}</p>
                  )}
                </div>
              </div>
            );
          })}

          <p className="text-xs font-medium text-muted-foreground mt-5 mb-2">NAICS codes</p>
          <div className="bg-surface border border-border rounded-xl px-4 py-3 space-y-3" data-testid="naics-block">
            <NaicsSuggestionsBox company={company} onChanged={handleNaicsDecided} />
            <NaicsCodesInput codes={codes} onChange={setCodes} disabled={saving} />
            {(company.naics_codes ?? []).length === 0 && (
              <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer" title="Tick when you have looked and no NAICS code could be found — it satisfies the research gate and changes nothing in the checks">
                <input
                  type="checkbox"
                  checked={company.naics_reviewed_at != null}
                  disabled={naicsReviewSaving}
                  onChange={(e) => markNaicsReviewed(e.target.checked)}
                  className="accent-primary"
                  data-testid="naics-researched-none"
                />
                Researched — nothing found
              </label>
            )}
            {naicsReviewError && <Alert tone="danger">{naicsReviewError}</Alert>}
            {saveError && (
              <Alert tone="danger">{saveError}</Alert>
            )}
            {codes.length === 0 && (
              <Alert tone="warning" className="text-[11px]">Add at least one NAICS code before saving — an empty list would exclude every Prime at this company from every future RFP.</Alert>
            )}
            <div className="flex justify-end">
              <Button variant="primary" size="sm" onClick={saveCapability} disabled={saving || codes.length === 0}>
                {saving ? "Saving…" : saved ? "Saved" : "Save"}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between gap-2 mb-2">
          <p className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
            <Users className="w-3.5 h-3.5" /> Contacts at this company
          </p>
          <Button variant="outline" size="sm" onClick={() => setAddContactOpen(true)} title="Add a contact person to this company by hand">
            <UserPlus className="w-3.5 h-3.5" /> Add contact
          </Button>
        </div>
        <div className={`${TABLE_SHELL} overflow-x-auto`}>
          <table className="w-full min-w-[500px]">
            <thead>
              <tr className={THEAD_ROW}>
                <th className={TH} title="The company's primary contact — outreach uses this person first">Primary</th>
                <th className={TH}>Name</th>
                <th className={TH}>Title</th>
                <th className={TH}>Email</th>
                <th className={TH}>Phone</th>
                <th className={TH}>Appearances</th>
                <th className={TH}><span className="sr-only">Remove</span></th>
              </tr>
            </thead>
            <tbody>
              {company.prime_contacts.map((p) => (
                <tr key={p.id} id={rowDomId("prime", p.id)} className={`${TBODY_ROW} ${highlightId === p.id ? ROW_HIGHLIGHT_CLASS : ""}`}>
                  <td className={`${TD} text-sm`}>
                    <PrimaryContactToggle contact={p} onChanged={handlePrimaryChanged} />
                  </td>
                  <td className={`${TD} text-sm`}>
                    <Link to={`${crmBasePath}/prime-contacts/${p.id}`} state={rowLinkState(location)} className="text-primary hover:underline inline-flex items-center gap-1.5">
                      {p.status === "do_not_contact" && (
                        <span
                          title={`Do Not Contact${p.status_reason ? `: ${p.status_reason}` : ""} — this person only, not the whole company`}
                        >
                          <Ban className="w-3 h-3 text-destructive flex-shrink-0" />
                        </span>
                      )}
                      {p.status === "bounced" && (
                        <span
                          title={`Bounced${p.status_reason ? `: ${p.status_reason}` : ""} — this address only, not the whole company`}
                        >
                          <MailWarning className="w-3 h-3 text-warning-soft-foreground flex-shrink-0" />
                        </span>
                      )}
                      {[p.first_name, p.last_name].filter(Boolean).join(" ") || p.primary_email}
                      {p.last_replied_at && (
                        <span className="text-xs text-success-soft-foreground" title={`Replied ${formatDate(p.last_replied_at, true)}`}>Replied</span>
                      )}
                    </Link>
                  </td>
                  <td className={`${TD} text-sm text-foreground`}>{p.title || "—"}</td>
                  <td className={`${TD} text-sm`}>
                    <a href={`mailto:${p.primary_email}`} className="text-muted-foreground hover:text-foreground">{p.primary_email}</a>
                  </td>
                  <td className={`${TD} text-sm text-foreground`}>{p.phone || "—"}</td>
                  <td className={`${TD} text-sm text-foreground`}>{p.appearance_count}</td>
                  <td className={`${TD} text-right`}>
                    <button
                      type="button"
                      onClick={() => setRemoveContactId(p.id)}
                      title="Remove this contact from the company"
                      aria-label={`Remove ${[p.first_name, p.last_name].filter(Boolean).join(" ") || p.primary_email}`}
                      className="p-1 rounded-md text-muted-foreground/50 hover:text-destructive transition-colors"
                      data-testid={`remove-contact-${p.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
              {company.prime_contacts.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-sm text-muted-foreground">
                    No contacts linked to this company yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <CompanyOutreachSection outreach={company.outreach} />

      <div>
        <p className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1.5">
          <FileText className="w-3.5 h-3.5" /> RFPs this company has appeared on ({company.opportunities.length})
        </p>
        <div className={`${TABLE_SHELL} overflow-x-auto`}>
          <table className="w-full min-w-[800px]">
            <thead>
              <tr className={THEAD_ROW}>
                <th className={TH}>Title</th>
                <th className={TH}>Company's role</th>
                <th className={TH}>Plan holder</th>
                <th className={TH}>CollabP screen</th>
                <th className={TH}>Stage</th>
                <th className={TH}>Agency</th>
                <th className={TH}>Contacts</th>
              </tr>
            </thead>
            <tbody>
              {company.opportunities.map((o) => (
                <tr key={o.id} className={TBODY_ROW}>
                  <td className={`${TD} text-sm`}>
                    <Link to={`${crmBasePath}?opportunity_id=${o.id}`} className="text-primary hover:underline">
                      {o.title}
                    </Link>
                  </td>
                  <td className={`${TD} text-sm`}>
                    {o.contractor_type ? <ContractorTypeBadge type={o.contractor_type} /> : <span className="text-muted-foreground">—</span>}
                    {o.contractor_type === ContractorType.Unconfirmed && (
                      <RoleVerdictButtons opportunityId={o.id} companyId={company.id} companyName={company.name ?? "this company"} onDecided={load} />
                    )}
                  </td>
                  <td className={`${TD} text-sm text-foreground`}>
                    {o.plan_holder_status || "—"}
                    {o.documents_downloaded_at && (
                      <div className="text-xs text-muted-foreground">Downloaded {formatDownloaded(o.documents_downloaded_at)}</div>
                    )}
                  </td>
                  <td className={`${TD} text-sm text-foreground`} title={o.screen_reason || undefined}>
                    {o.screen_verdict || "—"}
                  </td>
                  <td className={`${TD} text-sm text-foreground`}>{o.stage_name}</td>
                  <td className={`${TD} text-sm text-foreground`}>{o.rfp?.agency_name || "—"}</td>
                  <td className={`${TD} text-sm text-foreground`}>{o.contact_count}</td>
                </tr>
              ))}
              {company.opportunities.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-sm text-muted-foreground">
                    No RFPs on file for this company yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {servicesOpen && (
        <EditServiceCategoriesModal company={company} onClose={() => setServicesOpen(false)} onSaved={handleEditSaved} onStale={() => { setServicesOpen(false); load(); }} />
      )}
      {credentialsOpen && (
        <EditCredentialsModal company={company} onClose={() => setCredentialsOpen(false)} onSaved={handleEditSaved} onStale={() => { setCredentialsOpen(false); load(); }} />
      )}
      {textListOpen && (
        <EditTextListModal company={company} field={textListOpen} onClose={() => setTextListOpen(null)} onSaved={handleEditSaved} onStale={() => { setTextListOpen(null); load(); }} />
      )}
      {capabilityOpen && (
        <EditCapabilityStatementsModal company={company} onClose={() => setCapabilityOpen(false)} onSaved={handleEditSaved} onStale={() => { setCapabilityOpen(false); load(); }} />
      )}
      {addressesOpen && (
        <EditAddressesModal company={company} onClose={() => setAddressesOpen(false)} onSaved={handleEditSaved} onStale={() => { setAddressesOpen(false); load(); }} />
      )}
      {editOpen && (
        <EditCompanyModal company={company} onClose={() => setEditOpen(false)} onSaved={handleEditSaved} />
      )}
      {removeContactId !== null && (
        <RemoveContactModal
          contactId={removeContactId}
          onClose={() => setRemoveContactId(null)}
          onRemoved={(result) => {
            setRemoveContactId(null);
            // refetch quietly (no spinner): the contact list, the primary mark, the delete impact and RFP contact counts all changed
            getPrimeCompany(result.company_id).then((data) => { setCompany(data); setCodes(data.naics_codes ?? []); }).catch(() => load());
          }}
        />
      )}
      {addContactOpen && (
        <AddPrimeContactModal
          company={{ id: company.id, name: company.name }}
          onClose={() => setAddContactOpen(false)}
          onSaved={(created) => navigate(`${crmBasePath}/prime-contacts/${created.id}`)}
        />
      )}
      {skipStatusOpen && (
        <CompanySkipStatusModal
          companyId={company.id}
          companyName={company.name || "this company"}
          status={skipStatusOpen}
          onClose={() => setSkipStatusOpen(null)}
          onSaved={() => { setSkipStatusOpen(null); load(); }}
        />
      )}
      {deleteOpen && (
        <DeleteCompanyModal
          company={company}
          onClose={() => setDeleteOpen(false)}
          // back to the Companies list (with its filters when that is where we came from) — never to a page that may
          // belong to a person or RFP link that was just deleted
          onDeleted={() => navigate(/\/companies(\?|$)/.test(backTo.to) ? backTo.to : `${crmBasePath}/companies`, { replace: true })}
        />
      )}
    </div>
  );
}
